import { useState } from "react";
import { Check, Sparkles } from "lucide-react";
import { PLANS, CONTACT } from "../data/mock";

const buildWhatsAppLink = (plan) => {
  const text = encodeURIComponent(
    `Hi Reptile Hisabbook team, I'd like to subscribe to the ${plan.name} plan (${plan.price}${plan.period}). Please share the next steps.`
  );
  return `https://wa.me/${CONTACT.whatsapp}?text=${text}`;
};

export const Pricing = () => {
  // Re-order plans so highlighted (yearly) sits in the visual middle on desktop
  const ordered = [...PLANS].sort((a, b) => {
    const order = { monthly: 0, yearly: 1, halfyearly: 2 };
    return order[a.id] - order[b.id];
  });

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <span className="text-xs tracking-[0.22em] uppercase font-semibold text-orange-500">
            Simple, transparent pricing
          </span>
          <h2
            data-testid="pricing-headline"
            className="font-heading mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900"
          >
            One app. All features.{" "}
            <span className="text-teal-600">Pick your plan.</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            Every plan unlocks <strong>100% of the features</strong> — only the
            billing period and price change. No hidden charges, ever.
          </p>
        </div>

        <div
          data-testid="pricing-grid"
          className="grid md:grid-cols-3 gap-6 md:gap-7 items-stretch"
        >
          {ordered.map((plan) => (
            <PricingCard key={plan.id} plan={plan} />
          ))}
        </div>

        <p className="mt-10 text-center text-sm text-slate-500">
          Need a custom enterprise plan for multiple stores or warehouses?{" "}
          <a
            href="#contact"
            className="font-semibold text-teal-700 hover:text-orange-500"
            data-testid="pricing-contact-link"
          >
            Talk to us →
          </a>
        </p>
      </div>
    </section>
  );
};

const PricingCard = ({ plan }) => {
  const [hover, setHover] = useState(false);

  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`relative rounded-3xl p-7 md:p-8 flex flex-col transition-all duration-300 ${
        plan.highlight
          ? "bg-slate-900 text-white border-2 border-orange-500 shadow-2xl shadow-orange-500/20 md:-translate-y-3 md:scale-[1.02]"
          : "bg-white border border-slate-200 text-slate-900 hover:border-teal-300 hover:shadow-xl hover:-translate-y-1"
      }`}
    >
      {plan.badge && (
        <div
          data-testid={`plan-badge-${plan.id}`}
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-orange-500 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-white shadow-lg shadow-orange-500/30"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {plan.badge}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3
          className={`font-heading text-xl font-bold ${
            plan.highlight ? "text-white" : "text-slate-900"
          }`}
        >
          {plan.name}
        </h3>
        <span
          className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${
            plan.highlight ? "text-orange-400" : "text-teal-700"
          }`}
        >
          All Features
        </span>
      </div>

      <div className="mt-5 flex items-baseline gap-1.5">
        <span
          data-testid={`plan-price-${plan.id}`}
          className={`font-heading text-5xl md:text-6xl font-extrabold tracking-tight ${
            plan.highlight ? "text-white" : "text-slate-900"
          }`}
        >
          {plan.price}
        </span>
        <span
          className={`text-sm font-medium ${
            plan.highlight ? "text-slate-300" : "text-slate-500"
          }`}
        >
          {plan.period}
        </span>
      </div>

      <p
        className={`mt-1.5 text-sm ${
          plan.highlight ? "text-slate-300" : "text-slate-500"
        }`}
      >
        {plan.note}
      </p>

      <div
        className={`my-6 h-px ${
          plan.highlight ? "bg-white/15" : "bg-slate-200"
        }`}
      />

      <ul className="space-y-3 flex-1">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span
              className={`mt-0.5 h-5 w-5 shrink-0 rounded-full flex items-center justify-center ${
                plan.highlight
                  ? "bg-orange-500/20 text-orange-400"
                  : "bg-teal-100 text-teal-700"
              }`}
            >
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            <span
              className={`text-sm ${
                plan.highlight ? "text-slate-200" : "text-slate-700"
              }`}
            >
              {f}
            </span>
          </li>
        ))}
      </ul>

      <a
        href={buildWhatsAppLink(plan)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`plan-cta-${plan.id}`}
        className={`mt-8 inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-semibold transition-all active:scale-95 ${
          plan.highlight
            ? "bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/30"
            : "bg-slate-900 hover:bg-slate-800 text-white"
        }`}
      >
        {plan.cta}
      </a>
    </div>
  );
};

export default Pricing;
